import { useEffect, useRef } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Task, TrackingEventEnum, TrackingEventsPublicType } from '@trnsact/trnsact-shared-types';
import { useTrackingEvents } from '../../../hooks/useTrackingEvents';
import { InsuranceQuoteForm, LoadingSkeleton } from './ui';
import { useInsuranceQuoteDataFetch } from './hooks/useInsuranceQuoteDataFetch';
import { InsuranceQuoteFormValues } from './types';
import { getDynamicPricingInput } from './lib';
import { useInsuranceQuoteSubmit } from './hooks/useInsuranceQuoteSubmit';
import { TaskStatuses } from '../../../api/types';

interface Props {
  task: Task;
  refetchTask: any;
  publicAccount: any;
}

export const InsuranceQuotePage = ({ task, publicAccount, refetchTask }: Props) => {
  const classes = useStyles();

  const { createTrackingEvent } = useTrackingEvents();
  const hasTrackedPageLoad = useRef(false);

  const {
    uniqueAssets,
    uniqueProgramCodes,
    creditApplication,
    insuranceQuote,
    isLoading: isInsuranceQuoteDataLoading,
  } = useInsuranceQuoteDataFetch(task);

  useEffect(() => {
    if (!creditApplication || hasTrackedPageLoad.current) return;
    hasTrackedPageLoad.current = true;
    createTrackingEvent({
      variables: {
        input: {
          eventType: TrackingEventsPublicType.InsuranceExpress,
          event: TrackingEventEnum.InsuranceQuotePageLoaded,
          eventData: { creditApplicationId: creditApplication.id, taskId: task.taskId },
          accountId: publicAccount.id,
        },
      },
    });
  }, [creditApplication]);

  const { submit, isSubmitLoading, isErrorOnSubmit } = useInsuranceQuoteSubmit(refetchTask);

  const onSubmit = async (values: InsuranceQuoteFormValues) => {
    const dynamicPricingInput = getDynamicPricingInput(values, publicAccount.vendorProfile, creditApplication);
    const proposalId = await submit(
      task.taskId!,
      insuranceQuote!.insuranceQuoteId,
      dynamicPricingInput,
      creditApplication!,
      publicAccount
    );
    if (proposalId) {
      const proposalPageUrl = `/ps/?proposalId=${proposalId}&vendorGUID=${publicAccount.dynamicsAccountId}`;
      window.location.href = proposalPageUrl;
    }
  }

  if (isInsuranceQuoteDataLoading) {
    return <LoadingSkeleton />;
  }

  if (isSubmitLoading) {
    return <Box>
      <LoadingSkeleton />
      <Typography sx={{ mt: 2 }} align='center'>Please don’t leave this page. You’ll be redirected once your insurance quote is generated.</Typography>
    </Box>;
  }

  if (task.status === TaskStatuses.Complete) {
    return <Alert severity="success">Your insurance quote has been successfully generated!</Alert>
  }

  return (
    <>
      <Typography variant="h2" className={classes.header}>
        Insurance Quote Request
      </Typography>
      <Box>
        <InsuranceQuoteForm
          uniqueAssets={uniqueAssets}
          uniqueProgramCodes={uniqueProgramCodes}
          creditApplication={creditApplication}
          publicAccount={publicAccount}
          task={task}
          onSubmit={onSubmit}
        />
        {isErrorOnSubmit && <Alert sx={{ mt: 2 }} severity="error">Pricing cannot be set. Please check the data you entered for the product and try again.</Alert>}
      </Box>
    </>
  );
};

const useStyles = makeStyles({
  header: {
    '&.MuiTypography-root': {
      fontWeight: 500,
      fontSize: 24,
      textAlign: 'center',
      marginBottom: 32,
    },
  },
});
